import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Users } from 'lucide-react';

interface GuestSelectorProps {
  value: number | null;
  maxGuests?: number;
  onChange: (count: number) => void;
}

const GuestSelector: React.FC<GuestSelectorProps> = ({ value, maxGuests = 10, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const buttons = Array.from({ length: Math.min(maxGuests, 10) }, (_, i) => i + 1);

  const handleSelect = (n: number) => {
    setShowCustom(false);
    onChange(n);
  };

  const handleCustom = () => {
    setShowCustom(true);
  };

  const handleCustomSubmit = () => {
    const v = parseInt(customValue);
    if (!isNaN(v) && v > 0 && v <= maxGuests) onChange(v);
  };

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Users className="h-5 w-5" />
        <span className="text-sm font-medium">Combien de convives ?</span>
      </div>

      <div className="grid grid-cols-5 gap-3 w-full max-w-xs">
        {buttons.map(n => (
          <motion.button
            key={n}
            type="button"
            whileTap={{ scale: 0.9 }}
            animate={value === n ? { scale: [1, 1.15, 1] } : {}}
            transition={{ duration: 0.2 }}
            onClick={() => handleSelect(n)}
            className={cn(
              'aspect-square rounded-xl text-lg font-bold transition-all min-h-[52px]',
              'focus:outline-none focus:ring-2 focus:ring-primary/50',
              value === n
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'bg-muted hover:bg-muted/80 text-foreground'
            )}
          >
            {n}
          </motion.button>
        ))}
      </div>

      {maxGuests > 10 && (
        <>
          {!showCustom ? (
            <motion.button
              type="button"
              onClick={handleCustom}
              className="text-sm text-primary font-medium underline underline-offset-4"
              whileTap={{ scale: 0.95 }}
            >
              Plus de 10 personnes
            </motion.button>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={11}
                  max={maxGuests}
                  placeholder="Nombre"
                  value={customValue}
                  onChange={e => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val > maxGuests) {
                      setCustomValue(String(maxGuests));
                    } else {
                      setCustomValue(e.target.value);
                    }
                  }}
                  className="w-24 text-center"
                  autoFocus
                />
                <motion.button
                  type="button"
                  onClick={handleCustomSubmit}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
                  whileTap={{ scale: 0.95 }}
                >
                  OK
                </motion.button>
              </div>
              <span className="text-xs text-muted-foreground">Maximum {maxGuests} personnes</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GuestSelector;
